import {
  AGENT_511_LOCATION,
  AGENT_511_ADR,
} from '@rsos/capstone/src/constants/capabilities';
import { AGENT_511 as AGENT_511_PERMISSION } from '@rsos/capstone/src/constants/permissionNames';
import {
  SOURCE_ADMIN_SMS,
  SOURCE_CITIZEN_LOCATION,
  SOURCE_RCS,
  SOURCE_SMS,
  SOURCE_TCAPI,
} from '@rsos/constants/locationSources';
import { TEXT } from '@rsos/constants/locationTypes';
import {
  RCS_ACCEPT_STATUS_DECLINED,
  RCS_ACCEPT_STATUS_TIMED_OUT,
} from '@rsos/constants/rcsStatuses';
import { getMostRecentLoc } from '@rsos/utils/getMostRecentLoc';
import { selectConversationUnread } from '../conversations/conversationsSlice';
import { EmergencyCallSecondaryLocation } from '../emergencyCalls/emergencyCalls.types';
import { selectCallData } from '../emergencyCalls/emergencyCallsSlice';
import {
  selectRcsConversation,
  selectUnreadRcsMessages,
} from '../rcs/rcsSelectors';
import { selectTextFrom911Conversation } from '../textFrom911/textFrom911Slice';
import { AudioNotificationsGlobalState } from './audioNotificationTypes';

export function selectIsSMS(
  state: AudioNotificationsGlobalState,
  callerID: string,
) {
  const call = selectCallData(state, callerID);
  const tf911 = selectTextFrom911Conversation(state, callerID);
  const mostRecentLocation = getMostRecentLoc(call);
  return (
    call?.isTT911 ||
    !!tf911?.data?.conversation_id ||
    isTextBasedSource(mostRecentLocation?.source) ||
    // Casting here rather than a type guard, which would be longer
    (mostRecentLocation as EmergencyCallSecondaryLocation)
      ?.class_of_service_category === TEXT
  );
}

const textBasedSources = new Set([
  SOURCE_SMS,
  SOURCE_RCS,
  SOURCE_TCAPI,
  SOURCE_ADMIN_SMS,
  SOURCE_CITIZEN_LOCATION,
]);

function isTextBasedSource(source = '') {
  return textBasedSources.has(source);
}

export function selectHasAgent511Access(state: AudioNotificationsGlobalState) {
  const isLocationCapabilityEnabled =
    !!state.psaps?.currentPSAP?.activeCapabilities?.[AGENT_511_LOCATION];
  const isADRCapabilityEnabled =
    !!state.psaps?.currentPSAP?.activeCapabilities?.[AGENT_511_ADR];
  const isPermissionEnabled =
    !!state.sinatra?.user?.currentRole?.permissions?.includes(
      AGENT_511_PERMISSION,
    );
  return (
    isLocationCapabilityEnabled && isADRCapabilityEnabled && isPermissionEnabled
  );
}

export function selectHasUnreadTextMessage(
  state: AudioNotificationsGlobalState,
  callerID: string,
) {
  const rcs = selectUnreadRCSMessage(state, callerID);
  const tf911 = selectHasUnreadTF911Message(state, callerID);
  const conversation = selectConversationUnread(callerID);

  return rcs || tf911 || conversation;
}

function selectUnreadRCSMessage(
  state: AudioNotificationsGlobalState,
  callerID: string,
) {
  const rcsConversation = selectRcsConversation(state, callerID);
  const rcsUnreadMessages = selectUnreadRcsMessages(state);
  if (
    rcsConversation?.data?.accept_status === RCS_ACCEPT_STATUS_TIMED_OUT ||
    rcsConversation?.data?.accept_status === RCS_ACCEPT_STATUS_DECLINED
  ) {
    return false;
  }

  const separateTracking = rcsUnreadMessages[callerID];
  if (separateTracking !== undefined) {
    return separateTracking;
  }

  if (!rcsConversation || !rcsConversation.ui) return false;

  const { unseenMessage } = rcsConversation.ui;
  return unseenMessage;
}

function selectHasUnreadTF911Message(
  state: AudioNotificationsGlobalState,
  callerID: string,
) {
  const textFrom911Data = selectTextFrom911Conversation(state, callerID);
  if (!textFrom911Data || !textFrom911Data.ui) return false;
  return textFrom911Data.ui.hasUnreadMessages || false;
}
